<template>
  <main class="mypage-redesign">
    <!-- 메인 콘텐츠 영역 -->
    <div class="mypage-container">
      <!-- MypageSidebar 컴포넌트 -->
      <MypageSidebar />

      <!-- 메인 콘텐츠 -->
      <div class="main-content">
        <!-- 다운로드 내역 섹션 -->
        <section class="download-history-section">
          <div class="section-header">
            <div>
              <h2 class="section-title">다운로드 내역</h2>
              <p class="section-description">콘텐츠 다운로드 기록을 확인하실 수 있습니다</p>
            </div>
          </div>

          <!-- 날짜 필터 -->
          <div class="filter-section">
            <div class="filter-row">
              <label class="filter-label">기간조회</label>
              <VDatePicker v-model.range="range" locale="ko" :max-date="new Date()">
                <template #default="{ inputValue, inputEvents }">
                  <div class="date-picker-wrapper">
                    <input type="text" class="date-input" :value="inputValue.start" v-on="inputEvents.start" />
                    <span class="date-separator">~</span>
                    <input type="text" class="date-input" :value="inputValue.end" v-on="inputEvents.end" />
                    <button class="search-button" type="button" @click="getdownloadHistory(1)">조회</button>
                  </div>
                </template>
              </VDatePicker>
            </div>
          </div>

          <!-- 다운로드 내역 리스트 -->
          <div v-if="downloadHistory.length !== 0" class="download-list">
            <div v-for="item in downloadHistory" :key="item.downloadHistoryId" class="download-item-card">
              <div class="download-icon">⬇️</div>
              <div class="download-info">
                <p class="download-date">{{ formDownloadDate(item.downloadDateTime) }}</p>
                <h3 class="download-title">
                  {{ item.orderContentTitle }}
                  <span class="download-type">({{ item.downloadType }})</span>
                </h3>
              </div>
            </div>

            <!-- 페이지네이션 -->
            <pagination v-if="pageInfo.page" :pageInfo="pageInfo" :page="page" :size="size" @updatePage="updatePage" />
          </div>

          <!-- 빈 상태 -->
          <div v-else class="empty-state">다운로드 내역이 없습니다.</div>
        </section>
      </div>
    </div>
  </main>
</template>

<script setup>
import axios from "axios";
import { onMounted, ref, computed } from "vue";
import MypageSidebar from "@/components/MypageSidebar.vue";
import Pagination from "@/components/Pagination.vue";

const formatDate = (date) => date.toISOString().split("T")[0];

const start = new Date();
const end = new Date();
start.setDate(end.getDate() - 14);

const range = ref({ start, end });

const formStartDate = computed(() => formatDate(range.value.start));
const formEndDate = computed(() => formatDate(range.value.end));

const downloadHistory = ref([]);

const getdownloadHistory = async (num) => {
  try {
    const pageValue = num !== undefined ? 1 : page.value;

    const { data } = await axios.get(`/new_api/download/history/list?startDate=${formStartDate.value}&endDate=${formEndDate.value}&page=${pageValue}&size=${size.value}`);
    downloadHistory.value = data.data.downloadHistoryList;
    pageInfo.value = data.data.pageInfo;
  } catch (err) {
    console.error(err);
  }
};

onMounted(async () => {
  await getdownloadHistory();
});

const formDownloadDate = (d) => {
  const options = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" };
  const date = new Date(d);
  return date.toLocaleDateString("ko-KR", options).replace(/(\d+)\D(\d+)\D(\d+)\D(\d+)\D(\d+)/, "$1.$2.$3 $4:$5:$6");
};

const pageInfo = ref({});

const page = ref(1);
const size = ref(10);

const updatePage = (num) => {
  page.value = num;
  getdownloadHistory();
};
</script>

<style scoped lang="scss">
/* ========================================
   Variables & Reset
   ======================================== */
.mypage-redesign {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0;
  font-family: "Pretendard", "Malgun Gothic", sans-serif;
}

/* ========================================
   Main Container
   ======================================== */
.mypage-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  display: grid;
  grid-template-columns: 230px 1fr;
  gap: 40px;

  @media (max-width: 1023px) {
    grid-template-columns: 240px 1fr;
    gap: 32px;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    padding: 20px 16px 40px;
  }
}

/* ========================================
   Main Content
   ======================================== */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* ========================================
   Download History Section
   ======================================== */
.download-history-section {
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 767px) {
    padding: 24px 16px;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #222;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    font-size: 20px;
  }
}

.section-description {
  font-size: 14px;
  color: #888;
  margin: 0;
}

/* ========================================
   Filter Section
   ======================================== */
.filter-section {
  background: #f5f7fa;
  border: 1px solid #dee0e3;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    padding: 16px;
  }
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  min-width: 80px;

  @media (max-width: 767px) {
    min-width: auto;
  }
}

.date-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
}

.date-input {
  flex: 1;
  min-width: 140px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 8px;
  font-size: 14px;
  color: #555961;
  transition: all 0.2s;

  &:hover {
    border-color: #0c3388;
  }

  &:focus {
    outline: none;
    border-color: #0c3388;
    box-shadow: 0 0 0 3px rgba(12, 51, 136, 0.1);
  }

  @media (max-width: 767px) {
    min-width: 0;
  }
}

.date-separator {
  font-size: 14px;
  color: #555961;
  flex-shrink: 0;

  @media (max-width: 767px) {
    display: none;
  }
}

.search-button {
  padding: 12px 24px;
  background: #0c3388;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #092968;
  }

  &:focus-visible {
    outline: 2px solid #0c3388;
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
}

/* ========================================
   Download List
   ======================================== */
.download-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.download-item-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 12px;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 767px) {
    padding: 16px;
  }
}

.download-icon {
  font-size: 24px;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8eef8;
  border-radius: 8px;
}

.download-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.download-date {
  font-size: 14px;
  color: #888;
  margin: 0;
}

.download-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 767px) {
    font-size: 14px;
  }
}

.download-type {
  font-size: 14px;
  font-weight: 400;
  color: #666;

  @media (max-width: 767px) {
    font-size: 13px;
  }
}

/* ========================================
   Empty State
   ======================================== */
.empty-state {
  padding: 60px 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 12px;
}

/* ========================================
   Focus Styles
   ======================================== */
*:focus-visible {
  outline: 2px solid #0c3388;
  outline-offset: 2px;
}
</style>
